import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiService } from '../services/api';
import { FeedbackItem, FeedbackImage } from '../types';

const FEEDBACK_TYPE_STYLES: Record<string, string> = {
  positive: 'bg-green-100 text-green-800',
  negative: 'bg-red-100 text-red-800',
  neutral: 'bg-yellow-100 text-yellow-800',
};

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;

const TOOLBAR_H = 56; // px — matches top toolbar height
const PAGE_SIZE = 100;

const FeedbackPage: React.FC = () => {
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [siteOptions, setSiteOptions] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [lightboxImage, setLightboxImage] = useState<FeedbackImage | null>(null);
  const [zoom, setZoom] = useState(1);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const lightboxRef = useRef<HTMLDivElement>(null);

  // Keyboard handlers
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxImage) setLightboxImage(null);
        else if (selected) setSelected(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxImage, selected]);

  const resetLightboxState = () => { setZoom(1); setNaturalSize(null); };

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    if (!selected || !lightboxImage) return;
    const images = selected.images;
    const idx = images.findIndex(img => img.id === lightboxImage.id);
    if (direction === 'prev' && idx > 0) { setLightboxImage(images[idx - 1]); resetLightboxState(); }
    if (direction === 'next' && idx < images.length - 1) { setLightboxImage(images[idx + 1]); resetLightboxState(); }
  }, [selected, lightboxImage]);

  useEffect(() => {
    if (!lightboxImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxImage, navigateLightbox]);

  // Scroll-to-zoom in lightbox
  useEffect(() => {
    const el = lightboxRef.current;
    if (!el || !lightboxImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP))));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [lightboxImage]);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getFeedbackList({
        page,
        page_size: PAGE_SIZE,
        ...(filterSite && { workspace: filterSite }),
        ...(filterUser && { username: filterUser }),
        ...(filterDateFrom && { date_from: filterDateFrom }),
        ...(filterDateTo && { date_to: filterDateTo }),
      });
      setFeedbackList(data.results);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
      setSiteOptions(data.filter_options.sites);
      setUserOptions(data.filter_options.users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, [page, filterSite, filterUser, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  // Any filter change restarts at page 1 — staying on page 7 of a narrower
  // result set would land the user on an empty page.
  const applyFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const hasFilters = filterSite || filterUser || filterDateFrom || filterDateTo;
  const clearFilters = () => {
    setFilterSite('');
    setFilterUser('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  };

  const openLightbox = (img: FeedbackImage) => { setLightboxImage(img); resetLightboxState(); };

  const currentIdx = lightboxImage && selected
    ? selected.images.findIndex(img => img.id === lightboxImage.id)
    : -1;

  // Compute real pixel dimensions so zooming affects actual layout (not just CSS transform).
  // At zoom=1 the image fits the available viewport; zoom<1 shrinks it, zoom>1 enlarges it (scrollable).
  const imgDisplaySize = useMemo(() => {
    if (!naturalSize) return null;
    const availW = window.innerWidth * 0.9;
    const availH = window.innerHeight * 0.9 - TOOLBAR_H - 40; // 40px caption space
    const fitScale = Math.min(availW / naturalSize.w, availH / naturalSize.h, 1);
    return {
      width: Math.round(naturalSize.w * fitScale * zoom),
      height: Math.round(naturalSize.h * fitScale * zoom),
    };
  }, [naturalSize, zoom]);

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="mb-6">
        <h1 className="m-0 mb-2 text-3xl font-semibold">Feedback</h1>
        <p className="text-gray-600 m-0">All feedback submitted by users in this region</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-5 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="bg-transparent border-0 text-red-600 cursor-pointer">Dismiss</button>
        </div>
      )}

      {/* Filters */}
      {(feedbackList.length > 0 || hasFilters) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5 flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase">Site</label>
            <select
              value={filterSite}
              onChange={e => { applyFilter(setFilterSite)(e.target.value); setFilterUser(''); }}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[160px]"
            >
              <option value="">All sites</option>
              {siteOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase">User</label>
            <select
              value={filterUser}
              onChange={e => applyFilter(setFilterUser)(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[140px]"
            >
              <option value="">All users</option>
              {userOptions.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase">From</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={e => applyFilter(setFilterDateFrom)(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase">To</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={e => applyFilter(setFilterDateTo)(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Clear filters
            </button>
          )}

          <span className="text-sm text-gray-400 ml-auto self-center">
            {totalCount} {totalCount === 1 ? 'entry' : 'entries'}{hasFilters && ' matched'}
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading...</div>
      ) : feedbackList.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p>{hasFilters ? 'No feedback matches the current filters.' : 'No feedback found for this region.'}</p>
        </div>
      ) : (
        <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">User</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Site</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Accession</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Usecase</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Type</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Images</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Created</th>
              <th className="px-4 py-3 text-left bg-slate-50 font-semibold text-gray-700 text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {feedbackList.map((fb) => (
              <tr key={fb.id} className="border-b border-gray-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{fb.username}</td>
                <td className="px-4 py-3 max-w-[150px]">
                  <div className="truncate text-sm" title={fb.workspace_name}>{fb.workspace_name}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {fb.additional_info?.accessionNumber || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  {fb.additional_info?.usecase || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  {fb.feedback_type ? (
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium capitalize ${FEEDBACK_TYPE_STYLES[fb.feedback_type] || 'bg-gray-100 text-gray-700'}`}>
                      {fb.feedback_type}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{fb.images.length}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDate(fb.created_at)}</td>
                <td className="px-4 py-3">
                  <button
                    className="bg-gray-600 hover:bg-gray-700 text-white py-1.5 px-3 rounded text-xs transition-colors"
                    onClick={() => setSelected(fb)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && !lightboxImage && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">Feedback #{selected.id}</h3>
              <button
                onClick={() => setSelected(null)}
                className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              {/* Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">User</div>
                    <div className="font-medium">{selected.username}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Site</div>
                    <div>{selected.workspace_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Accession</div>
                    <div>{selected.additional_info?.accessionNumber || <span className="text-gray-400">—</span>}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Usecase</div>
                    <div>{selected.additional_info?.usecase || <span className="text-gray-400">—</span>}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Type</div>
                    {selected.feedback_type ? (
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium capitalize ${FEEDBACK_TYPE_STYLES[selected.feedback_type] || 'bg-gray-100 text-gray-700'}`}>
                        {selected.feedback_type}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Created</div>
                    <div>{formatDate(selected.created_at)}</div>
                  </div>
                </div>
              </div>

              {/* Feedback JSON */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Feedback Data</h4>
                <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono overflow-auto max-h-[250px]">
                  {JSON.stringify(selected.feedback, null, 2)}
                </pre>
              </div>

              {/* Additional info */}
              {selected.additional_info && Object.keys(selected.additional_info).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Additional Info</h4>
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono overflow-auto max-h-[200px]">
                    {JSON.stringify(selected.additional_info, null, 2)}
                  </pre>
                </div>
              )}

              {/* Images */}
              {selected.images.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">
                    Images ({selected.images.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-3">
                    {selected.images.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => openLightbox(img)}
                        className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-400 aspect-square"
                      >
                        <img
                          src={img.url}
                          alt={img.original_filename || `Image ${img.id}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && selected && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-[1100]"
          onClick={() => setLightboxImage(null)}
        >
          {/* Top toolbar */}
          <div
            className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-black/60 z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-white/70 text-sm truncate max-w-[40%]">
              {lightboxImage.original_filename}
            </span>

            <div className="flex items-center gap-2">
              {/* Zoom out */}
              <button
                onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                disabled={zoom <= ZOOM_MIN}
                className="bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white w-8 h-8 rounded flex items-center justify-center text-lg font-bold transition-colors"
                title="Zoom out"
              >
                −
              </button>

              {/* Zoom level */}
              <button
                onClick={() => setZoom(1)}
                className="bg-white/10 hover:bg-white/20 text-white px-2 h-8 rounded text-xs font-mono transition-colors min-w-[52px] text-center"
                title="Reset zoom"
              >
                {Math.round(zoom * 100)}%
              </button>

              {/* Zoom in */}
              <button
                onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                disabled={zoom >= ZOOM_MAX}
                className="bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white w-8 h-8 rounded flex items-center justify-center text-lg font-bold transition-colors"
                title="Zoom in"
              >
                +
              </button>

              {/* Download */}
              <a
                href={lightboxImage.url}
                download={lightboxImage.original_filename || `image-${lightboxImage.id}`}
                onClick={(e) => e.stopPropagation()}
                className="bg-white/10 hover:bg-white/20 text-white w-8 h-8 rounded flex items-center justify-center transition-colors"
                title="Download"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
              </a>

              {/* Close */}
              <button
                onClick={() => setLightboxImage(null)}
                className="bg-white/10 hover:bg-white/20 text-white w-8 h-8 rounded flex items-center justify-center text-xl transition-colors ml-1"
                title="Close (Esc)"
              >
                &times;
              </button>
            </div>
          </div>

          {/* Prev button */}
          {currentIdx > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center text-xl transition-colors z-10"
            >
              ‹
            </button>
          )}

          {/* Image container — fixed to viewport, scrollable when zoomed in */}
          <div
            className="overflow-auto flex items-center justify-center"
            style={{
              width: '90vw',
              height: `calc(90vh - ${TOOLBAR_H + 40}px)`,
              marginTop: TOOLBAR_H,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.url}
              alt={lightboxImage.original_filename || `Image ${lightboxImage.id}`}
              onLoad={(e) => {
                const el = e.target as HTMLImageElement;
                setNaturalSize({ w: el.naturalWidth, h: el.naturalHeight });
              }}
              style={imgDisplaySize
                ? { width: imgDisplaySize.width, height: imgDisplaySize.height, transition: 'width 0.15s, height 0.15s', flexShrink: 0 }
                : { maxWidth: '90vw', maxHeight: `calc(90vh - ${TOOLBAR_H + 40}px)` }
              }
              className="rounded shadow-2xl"
            />
          </div>

          {/* Next button */}
          {currentIdx < selected.images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center text-xl transition-colors z-10"
            >
              ›
            </button>
          )}

          {/* Caption */}
          <div className="absolute bottom-3 left-0 right-0 text-center text-white/60 text-sm pointer-events-none">
            {currentIdx + 1} / {selected.images.length}
            {zoom !== 1 && <span className="ml-3 text-white/40">scroll or use +/− to zoom</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
